import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  runTransaction,
  query,
  where,
  onSnapshot,
  Transaction,
  arrayUnion,
} from "firebase/firestore";
import { db } from "./firebase";
import { storage } from "./firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  InventoryItem,
  StockAdjustment,
  PurchaseOrder,
  PurchaseOrderItem,
  PurchaseOrderDocument,
  PurchaseOrderHistoryEntry,
} from "../@types/inventory";
import { TeamMember } from "../@types/team";
import { PurchaseOrderStatus } from "../@types/enums";
import { createNotification } from "./user";
import { generateUid } from "./general";
import { formatPaymentMethod } from "@/app/_constants/paymentMethods";

// ---- Low Stock Notification ----

async function checkLowStock(
  userId: string,
  item: InventoryItem,
  newStock: number
) {
  if (
    item.reorderThreshold > 0 &&
    newStock <= item.reorderThreshold &&
    item.currentStock > item.reorderThreshold // only notify on the transition
  ) {
    const id = `low-stock-${item.uid}-${Date.now()}`;
    try {
      await createNotification(
        userId,
        id,
        `Low Stock: ${item.description}`,
        `${item.description} (${item.sku}) is at ${newStock} units, below the reorder threshold of ${item.reorderThreshold}.`,
        "/inventory",
        "View Inventory",
        false
      );
    } catch (error) {
      // Callers run this after their transaction has already committed, so a
      // failure here means the stock change is saved and only the courtesy
      // notification was lost. Never surface it as a failed save.
      console.error("Low stock notification failed:", error);
    }
  }
}

// ---- Inventory Items ----

export async function getInventory(userId: string): Promise<InventoryItem[]> {
  const inventoryCol = collection(db, "users", userId, "inventory");
  const snapshot = await getDocs(inventoryCol);
  return snapshot.docs
    .map((doc) => doc.data() as InventoryItem)
    .filter((item) => !item.archivedAt);
}

export async function getInventoryItem(
  userId: string,
  itemUid: string
): Promise<InventoryItem | null> {
  const docRef = doc(db, "users", userId, "inventory", itemUid);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return docSnap.data() as InventoryItem;
}

export async function createInventoryItem(
  userId: string,
  itemData: Omit<InventoryItem, "uid" | "createdAt" | "updatedAt">
): Promise<InventoryItem> {
  const uid = generateUid();
  const now = Timestamp.now();

  const item: InventoryItem = {
    ...itemData,
    uid,
    createdAt: now,
    updatedAt: now,
  };

  const docRef = doc(db, "users", userId, "inventory", uid);
  await setDoc(docRef, item);
  return item;
}

export async function updateInventoryItem(
  userId: string,
  item: InventoryItem
): Promise<void> {
  const docRef = doc(db, "users", userId, "inventory", item.uid);
  await updateDoc(docRef, {
    ...item,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteInventoryItem(
  userId: string,
  itemUid: string,
  createdBy: string = userId,
  teamMember?: TeamMember
): Promise<void> {
  // Soft-delete: keeps the doc so historical job materials still resolve.
  // Writes a stock adjustment that records the on-hand stock being written off,
  // and zeroes currentStock so the audit ledger stays consistent (adjustments
  // sum to currentStock). The doc itself is retained for historical resolution.
  const itemRef = doc(db, "users", userId, "inventory", itemUid);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(itemRef);
    if (!snap.exists()) return;
    const item = snap.data() as InventoryItem;
    const now = Timestamp.now();
    // Delta to bring stock to zero (negative when stock was on hand).
    const removed = -(item.currentStock ?? 0);
    tx.update(itemRef, { archivedAt: now, currentStock: 0, updatedAt: now });

    const adjustmentUid = generateUid();
    const adjustmentRef = doc(
      db,
      "users",
      userId,
      "stockAdjustments",
      adjustmentUid
    );
    tx.set(adjustmentRef, {
      uid: adjustmentUid,
      inventoryItemUid: itemUid,
      adjustmentType: "archived",
      quantity: removed,
      reason:
        item.currentStock > 0
          ? `Item archived from inventory (${item.currentStock} written off)`
          : "Item archived from inventory",
      createdAt: now,
      createdBy,
      ...(teamMember ? { teamMember } : {}),
      sku: item.sku,
      itemDescription: item.description,
    } satisfies StockAdjustment);
  });
}

export async function adjustStock(
  userId: string,
  itemUid: string,
  quantity: number,
  adjustmentType: StockAdjustment["adjustmentType"],
  reason: string,
  createdBy: string,
  teamMember?: TeamMember
): Promise<void> {
  let _lowStockCheck: { userId: string; item: InventoryItem; newStock: number } | null = null;

  await runTransaction(db, async (transaction) => {
    const itemRef = doc(db, "users", userId, "inventory", itemUid);
    const itemSnap = await transaction.get(itemRef);

    if (!itemSnap.exists()) {
      throw new Error("Inventory item not found");
    }

    const item = itemSnap.data() as InventoryItem;
    const newStock = item.currentStock + quantity;

    // Update stock (negative stock is allowed — indicates backorder needed)
    transaction.update(itemRef, {
      currentStock: newStock,
      updatedAt: Timestamp.now(),
    });

    // Create adjustment record
    const adjustmentUid = generateUid();
    const adjustmentRef = doc(
      db,
      "users",
      userId,
      "stockAdjustments",
      adjustmentUid
    );
    const adjustment: StockAdjustment = {
      uid: adjustmentUid,
      inventoryItemUid: itemUid,
      adjustmentType,
      quantity,
      reason,
      createdAt: Timestamp.now(),
      createdBy,
      ...(teamMember ? { teamMember } : {}),
      sku: item.sku,
      itemDescription: item.description,
    };
    transaction.set(adjustmentRef, adjustment);

    // Queue low stock check (runs after transaction)
    if (quantity < 0) {
      _lowStockCheck = { userId, item, newStock };
    }
  });

  if (_lowStockCheck) {
    await checkLowStock(_lowStockCheck.userId, _lowStockCheck.item, _lowStockCheck.newStock);
  }
}


// ---- Material delta application ----

/**
 * Applies a map of inventoryItemUid -> qty deltas within a parent transaction.
 *
 * Positive delta = decrement stock (material added to job).
 * Negative delta = increment stock (material removed from job).
 *
 * IMPORTANT: All reads must happen before the caller performs any writes
 * in the parent transaction. Call this BEFORE any transaction.update/set
 * in the parent, or use the returned `apply` function to perform the writes.
 *
 * Returns an `apply` function that performs the writes, and a `postCommit`
 * function that triggers low stock notifications after the transaction commits.
 */
export async function stageMaterialDelta(
  transaction: Transaction,
  userId: string,
  delta: Map<string, number>,
  createdBy: string,
  reason: string = "Material change",
  teamMember?: TeamMember,
  job?: { jobId: number; jobUid: string; mountIds?: Map<string, string[]> }
) {
  const snapshots: {
    uid: string;
    delta: number;
    item: InventoryItem;
    newStock: number;
  }[] = [];

  // All reads first
  for (const [inventoryItemUid, qtyDelta] of delta) {
    if (qtyDelta === 0) continue;
    const itemRef = doc(db, "users", userId, "inventory", inventoryItemUid);
    const itemSnap = await transaction.get(itemRef);
    if (!itemSnap.exists()) continue;

    const item = itemSnap.data() as InventoryItem;
    snapshots.push({
      uid: inventoryItemUid,
      delta: qtyDelta,
      item,
      newStock: item.currentStock - qtyDelta,
    });
  }

  return {
    apply() {
      for (const { uid, delta: qtyDelta, item, newStock } of snapshots) {
        const itemRef = doc(db, "users", userId, "inventory", uid);
        transaction.update(itemRef, {
          currentStock: newStock,
          updatedAt: Timestamp.now(),
        });

        const adjustmentUid = generateUid();
        const adjustmentRef = doc(
          db,
          "users",
          userId,
          "stockAdjustments",
          adjustmentUid
        );
        const mounts = job?.mountIds?.get(uid);
        transaction.set(adjustmentRef, {
          uid: adjustmentUid,
          inventoryItemUid: uid,
          adjustmentType:
            qtyDelta > 0 ? "material_assigned" : "material_removed",
          quantity: -qtyDelta,
          reason,
          createdAt: Timestamp.now(),
          createdBy,
          ...(teamMember ? { teamMember } : {}),
          ...(job ? { jobId: job.jobId, jobUid: job.jobUid } : {}),
          ...(mounts && mounts.length ? { mountIds: mounts } : {}),
          sku: item.sku,
          itemDescription: item.description,
        } satisfies StockAdjustment);
      }
    },
    async postCommit() {
      // Only check for decrements (positive delta = stock went down)
      for (const { delta: qtyDelta, item, newStock } of snapshots) {
        if (qtyDelta > 0) {
          await checkLowStock(userId, item, newStock);
        }
      }
    },
  };
}

// ---- Stock Adjustments ----

export async function getStockAdjustments(
  userId: string,
  inventoryItemUid?: string
): Promise<StockAdjustment[]> {
  const adjustmentsCol = collection(
    db,
    "users",
    userId,
    "stockAdjustments"
  );

  let snapshot;
  if (inventoryItemUid) {
    const q = query(
      adjustmentsCol,
      where("inventoryItemUid", "==", inventoryItemUid)
    );
    snapshot = await getDocs(q);
  } else {
    snapshot = await getDocs(adjustmentsCol);
  }

  return snapshot.docs.map((doc) => doc.data() as StockAdjustment);
}

// ---- Low Stock ----

export async function getItemsBelowThreshold(
  userId: string
): Promise<InventoryItem[]> {
  const items = await getInventory(userId);
  return items.filter(
    (item) =>
      item.reorderThreshold > 0 && item.currentStock <= item.reorderThreshold
  );
}

// ---- Purchase Orders ----

// Build a history entry. Store only the facts (resulting status + raw receive
// figures); the UI builds the text. Firestore rejects undefined, so only
// include fields when present.
function poHistory(
  teamMember?: TeamMember,
  status?: PurchaseOrderStatus,
  units?: number,
  amount?: number
): PurchaseOrderHistoryEntry {
  return {
    at: Timestamp.now(),
    ...(teamMember ? { teamMember } : {}),
    ...(status ? { status } : {}),
    ...(units != null ? { units } : {}),
    ...(amount != null ? { amount } : {}),
  };
}

export async function getPurchaseOrders(
  userId: string
): Promise<PurchaseOrder[]> {
  const col = collection(db, "users", userId, "purchaseOrders");
  const snapshot = await getDocs(col);
  return snapshot.docs.map((d) => d.data() as PurchaseOrder);
}

export async function createPurchaseOrder(
  userId: string,
  data: {
    supplierUid: string;
    supplierName: string;
    shopifyUrl?: string;
    items: Omit<PurchaseOrderItem, "quantityReceived">[];
    totalCost: number;
    discount?: number;
    discountType?: "percentage" | "fixed";
    notes: string;
    status: PurchaseOrderStatus;
    createdBy: string;
    teamMember?: TeamMember;
    // Backdate an order entered after the fact (e.g. catching up on January's
    // invoices). Drives createdAt/orderedAt so the order lands in the right
    // month; the history trail still records the real time it was keyed in.
    orderDate?: Date;
  }
): Promise<PurchaseOrder> {
  const uid = generateUid();
  const now = data.orderDate ? Timestamp.fromDate(data.orderDate) : Timestamp.now();
  const metaRef = doc(db, "users", userId, "metadata", "purchaseOrders");
  const orderRef = doc(db, "users", userId, "purchaseOrders", uid);

  // Atomically bump the sequential PO counter and write the order together.
  return runTransaction(db, async (transaction) => {
    const metaSnap = await transaction.get(metaRef);
    const orderNumber = (metaSnap.data()?.orderNum || 0) + 1;

    const order: PurchaseOrder = {
      uid,
      orderNumber,
      supplierUid: data.supplierUid,
      supplierName: data.supplierName,
      ...(data.shopifyUrl ? { shopifyUrl: data.shopifyUrl } : {}),
      items: data.items.map((item) => ({ ...item, quantityReceived: 0 })),
      totalCost: data.totalCost,
      ...(data.discount
        ? {
            discount: data.discount,
            discountType: data.discountType ?? "percentage",
          }
        : {}),
      status: data.status,
      notes: data.notes,
      createdAt: now,
      orderedAt: data.status === PurchaseOrderStatus.Ordered ? now : null,
      receivedAt: null,
      createdBy: data.createdBy,
      history: [
        poHistory(data.teamMember, data.status),
        ...(data.status === PurchaseOrderStatus.Ordered
          ? [poHistory(data.teamMember, PurchaseOrderStatus.Ordered)]
          : []),
      ],
    };

    transaction.set(metaRef, { orderNum: orderNumber }, { merge: true });
    transaction.set(orderRef, order);
    return order;
  });
}

export async function updatePurchaseOrder(
  userId: string,
  order: PurchaseOrder
): Promise<void> {
  const docRef = doc(db, "users", userId, "purchaseOrders", order.uid);
  await updateDoc(docRef, { ...order });
}

export async function markPurchaseOrderOrdered(
  userId: string,
  orderUid: string,
  teamMember?: TeamMember,
  // The date the order was actually placed with the supplier, when it differs
  // from today (backdated entry).
  orderDate?: Date
): Promise<void> {
  const docRef = doc(db, "users", userId, "purchaseOrders", orderUid);
  await updateDoc(docRef, {
    status: PurchaseOrderStatus.Ordered,
    orderedAt: orderDate ? Timestamp.fromDate(orderDate) : Timestamp.now(),
    history: arrayUnion(
      poHistory(teamMember, PurchaseOrderStatus.Ordered)
    ),
  });
}

export interface PurchaseOrderEdits {
  orderedAt?: Date;
  receivedAt?: Date;
  notes?: string;
  tax?: number;
  shipping?: number;
  discount?: number;
  discountType?: "percentage" | "fixed";
  paymentMethod?: PurchaseOrder["paymentMethod"] | null;
}

const money = (value: number) => `$${value.toFixed(2)}`;

// Correct an existing order after the fact. Two reasons this exists: orders
// keyed in later than they happened (the order date drives what the list shows
// and sorts by, the received date drives Money Spent), and invoice figures that
// came in wrong (tax/shipping/discount all feed the amount Money Spent counts).
// Diffed against the order we were given so only real changes are written, and
// the change is appended to the history trail (stamped with the real time it
// was made) so money moving months or totals is traceable.
export async function updatePurchaseOrderDetails(
  userId: string,
  order: PurchaseOrder,
  edits: PurchaseOrderEdits,
  teamMember?: TeamMember
): Promise<void> {
  const changed: string[] = [];
  const payload: Record<string, unknown> = {};

  const sameDay = (a: Date, b: Timestamp | null) =>
    !!b && a.toDateString() === b.toDate().toDateString();

  if (edits.orderedAt && !sameDay(edits.orderedAt, order.orderedAt)) {
    payload.orderedAt = Timestamp.fromDate(edits.orderedAt);
    changed.push(`order date to ${edits.orderedAt.toLocaleDateString()}`);
  }
  // Only an order that has actually been received carries a received date;
  // setting one here must never be what marks an order as arrived.
  if (
    order.receivedAt &&
    edits.receivedAt &&
    !sameDay(edits.receivedAt, order.receivedAt)
  ) {
    payload.receivedAt = Timestamp.fromDate(edits.receivedAt);
    changed.push(`received date to ${edits.receivedAt.toLocaleDateString()}`);
  }
  if (edits.notes != null && edits.notes !== order.notes) {
    payload.notes = edits.notes;
    changed.push("notes");
  }
  if (edits.tax != null && edits.tax !== (order.tax ?? 0)) {
    payload.tax = edits.tax;
    changed.push(`tax to ${money(edits.tax)}`);
  }
  if (edits.shipping != null && edits.shipping !== (order.shipping ?? 0)) {
    payload.shipping = edits.shipping;
    changed.push(`shipping to ${money(edits.shipping)}`);
  }

  const nextDiscount = edits.discount ?? 0;
  const nextType = edits.discountType ?? order.discountType ?? "percentage";
  const discountChanged =
    edits.discount != null &&
    (nextDiscount !== (order.discount ?? 0) ||
      (nextDiscount > 0 && nextType !== (order.discountType ?? "percentage")));
  if (discountChanged) {
    payload.discount = nextDiscount;
    payload.discountType = nextType;
    changed.push(
      `discount to ${
        nextType === "percentage" ? `${nextDiscount}%` : money(nextDiscount)
      }`
    );
  }

  if (
    edits.paymentMethod !== undefined &&
    (edits.paymentMethod ?? null) !== (order.paymentMethod ?? null)
  ) {
    payload.paymentMethod = edits.paymentMethod ?? null;
    changed.push(
      `payment method to ${formatPaymentMethod(edits.paymentMethod) ?? "none"}`
    );
  }

  if (!changed.length) return;

  const docRef = doc(db, "users", userId, "purchaseOrders", order.uid);
  await updateDoc(docRef, {
    ...payload,
    history: arrayUnion({
      ...poHistory(teamMember),
      // Free-text entry: the timeline renders `action` verbatim for anything
      // that is not a status change.
      action: `edited the order: changed the ${changed.join(", the ")}`,
    } satisfies PurchaseOrderHistoryEntry),
  });
}

export async function receivePurchaseOrderItems(
  userId: string,
  orderUid: string,
  receivedItems: { inventoryItemUid: string; quantity: number }[],
  createdBy: string,
  deductAsExpense?: boolean,
  // Final invoice details captured at receive time. unitPrices overrides the
  // per-item cost; tax/shipping/invoice are stored on the order so Money Spent
  // reflects the real total.
  finalize?: {
    unitPrices?: Record<string, number>;
    tax?: number;
    shipping?: number;
    // Order-level discount confirmed against the invoice. Overrides whatever
    // was entered at order creation (unlike tax/shipping, which accumulate).
    discount?: number;
    discountType?: "percentage" | "fixed";
    // How the invoice was paid. Stored on the order; a later receive that
    // sets one overwrites it.
    paymentMethod?: PurchaseOrder["paymentMethod"];
    // Documents attached during this receive; appended to the order's existing
    // documents so paperwork accumulates across receives.
    documents?: PurchaseOrderDocument[];
    // The date the goods actually arrived. Money Spent, Cash Flow and P&L all
    // bucket by receivedAt, so backdated entry has to set this or the spend
    // lands in the month the order was keyed in.
    receivedAt?: Date;
  },
  teamMember?: TeamMember
): Promise<void> {
  await runTransaction(db, async (transaction) => {
    // ── All reads first ──
    const orderRef = doc(db, "users", userId, "purchaseOrders", orderUid);
    const orderSnap = await transaction.get(orderRef);
    if (!orderSnap.exists()) throw new Error("Purchase order not found");

    const itemSnaps: { received: typeof receivedItems[0]; ref: any; snap: any }[] = [];
    for (const received of receivedItems) {
      if (received.quantity <= 0) continue;
      const itemRef = doc(db, "users", userId, "inventory", received.inventoryItemUid);
      const itemSnap = await transaction.get(itemRef);
      itemSnaps.push({ received, ref: itemRef, snap: itemSnap });
    }

    // ── All writes after ──
    const order = orderSnap.data() as PurchaseOrder;
    const updatedItems = order.items.map((item) => {
      // Apply an edited unit price if the user corrected it at receive time.
      const priceOverride = finalize?.unitPrices?.[item.inventoryItemUid];
      const withPrice =
        priceOverride !== undefined && priceOverride >= 0
          ? { ...item, unitPrice: priceOverride }
          : item;

      const received = receivedItems.find(
        (r) => r.inventoryItemUid === item.inventoryItemUid
      );
      if (!received) return withPrice;
      const newReceived = withPrice.quantityReceived + received.quantity;
      // Reject over-receive: catches stale tabs and concurrent receive races.
      if (newReceived > withPrice.quantity) {
        throw new Error(
          `Cannot receive ${received.quantity} of ${withPrice.description}: ${withPrice.quantityReceived} already received on a ${withPrice.quantity}-unit order. Refresh and try again.`
        );
      }
      return {
        ...withPrice,
        quantityReceived: newReceived,
      };
    });

    // Recompute the line-item subtotal (prices may have just been edited).
    const subtotal =
      Math.round(
        updatedItems.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0) * 100
      ) / 100;

    const allFullyReceived = updatedItems.every(
      (item) => item.quantityReceived >= item.quantity
    );
    const someReceived = updatedItems.some(
      (item) => item.quantityReceived > 0
    );

    // Any receive (partial or full) counts as money spent on a cash basis, so
    // we expense as goods arrive instead of all-at-once on full receive.
    const receivedSomethingNow = receivedItems.some((r) => r.quantity > 0);
    // Value of what was received this time, for the history entry.
    const unitsNow = receivedItems.reduce((s, r) => s + r.quantity, 0);
    const valueNow =
      Math.round(
        (receivedItems.reduce((sum, r) => {
          const it = updatedItems.find(
            (i) => i.inventoryItemUid === r.inventoryItemUid
          );
          return sum + (it ? it.unitPrice * r.quantity : 0);
        }, 0) +
          (finalize?.tax ?? 0) +
          (finalize?.shipping ?? 0)) *
          100
      ) / 100;
    const receiveStatus = allFullyReceived
      ? PurchaseOrderStatus.Received
      : PurchaseOrderStatus.PartiallyReceived;
    // Tax/shipping entered at receive are per-shipment, so accumulate them
    // across partial receives rather than overwriting.
    transaction.update(orderRef, {
      items: updatedItems,
      totalCost: subtotal,
      ...(finalize?.discount !== undefined
        ? {
            discount: finalize.discount,
            discountType: finalize.discountType ?? "percentage",
          }
        : {}),
      status: allFullyReceived
        ? PurchaseOrderStatus.Received
        : someReceived
        ? PurchaseOrderStatus.PartiallyReceived
        : order.status,
      ...(receivedSomethingNow
        ? {
            receivedAt: finalize?.receivedAt
              ? Timestamp.fromDate(finalize.receivedAt)
              : Timestamp.now(),
          }
        : order.receivedAt
        ? {}
        : { receivedAt: null }),
      ...(receivedSomethingNow && deductAsExpense !== undefined
        ? { deductedAsExpense: deductAsExpense }
        : {}),
      ...(finalize?.tax !== undefined
        ? { tax: (order.tax ?? 0) + finalize.tax }
        : {}),
      ...(finalize?.shipping !== undefined
        ? { shipping: (order.shipping ?? 0) + finalize.shipping }
        : {}),
      ...(finalize?.paymentMethod
        ? { paymentMethod: finalize.paymentMethod }
        : {}),
      ...(finalize?.documents && finalize.documents.length
        ? { documents: [...(order.documents ?? []), ...finalize.documents] }
        : {}),
      ...(receivedSomethingNow
        ? {
            history: [
              ...(order.history ?? []),
              poHistory(teamMember, receiveStatus, unitsNow, valueNow),
            ],
          }
        : {}),
    });

    for (const { received, ref, snap } of itemSnaps) {
      if (!snap.exists()) continue;

      const item = snap.data() as InventoryItem;
      transaction.update(ref, {
        currentStock: item.currentStock + received.quantity,
        updatedAt: Timestamp.now(),
      });

      const adjustmentUid = generateUid();
      const adjustmentRef = doc(
        db,
        "users",
        userId,
        "stockAdjustments",
        adjustmentUid
      );
      transaction.set(adjustmentRef, {
        uid: adjustmentUid,
        inventoryItemUid: received.inventoryItemUid,
        adjustmentType: "order_received",
        quantity: received.quantity,
        reason: `Received from purchase order ${orderUid}`,
        // Dated when the stock arrived, so the adjustment trail matches the
        // order for backdated receives.
        createdAt: finalize?.receivedAt
          ? Timestamp.fromDate(finalize.receivedAt)
          : Timestamp.now(),
        createdBy,
        ...(teamMember ? { teamMember } : {}),
        sku: item.sku,
        itemDescription: item.description,
      } satisfies StockAdjustment);
    }
  });
}

export async function cancelPurchaseOrder(
  userId: string,
  orderUid: string,
  teamMember?: TeamMember
): Promise<void> {
  const docRef = doc(db, "users", userId, "purchaseOrders", orderUid);
  await updateDoc(docRef, {
    status: PurchaseOrderStatus.Cancelled,
    history: arrayUnion(
      poHistory(teamMember, PurchaseOrderStatus.Cancelled)
    ),
  });
}

export async function deletePurchaseOrder(
  userId: string,
  orderUid: string
): Promise<void> {
  const docRef = doc(db, "users", userId, "purchaseOrders", orderUid);
  await deleteDoc(docRef);
}

// ---- Image Upload ----

export async function uploadInventoryItemImage(
  file: File,
  userId: string,
  itemUid: string
): Promise<string> {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const fileExtension = file.name.substring(file.name.lastIndexOf("."));
  const uniqueFileName = `${itemUid}_${timestamp}_${random}${fileExtension}`;

  const storageRef = ref(
    storage,
    `${userId}/inventory/${itemUid}/${uniqueFileName}`
  );
  const snapshot = await uploadBytes(storageRef, file);
  return getDownloadURL(snapshot.ref);
}

export async function uploadPurchaseOrderInvoice(
  file: File,
  userId: string,
  orderUid: string
): Promise<string> {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const fileExtension = file.name.substring(file.name.lastIndexOf("."));
  const uniqueFileName = `invoice_${orderUid}_${timestamp}_${random}${fileExtension}`;

  const storageRef = ref(
    storage,
    `${userId}/purchaseOrders/${orderUid}/${uniqueFileName}`
  );
  const snapshot = await uploadBytes(storageRef, file);
  return getDownloadURL(snapshot.ref);
}

// Human-readable order label. Falls back to the uid for orders created before
// sequential numbering existed.
export function getPurchaseOrderLabel(order: PurchaseOrder): string {
  return order.orderNumber ? `PO-${order.orderNumber}` : order.uid;
}

// All documents attached to an order, merging the legacy single-invoice fields
// so old orders still show their invoice.
export function getPurchaseOrderDocuments(
  order: PurchaseOrder
): PurchaseOrderDocument[] {
  const docs = [...(order.documents ?? [])];
  if (order.invoiceUrl && !docs.some((d) => d.url === order.invoiceUrl)) {
    docs.push({
      url: order.invoiceUrl,
      name: order.invoiceFileName || "Invoice",
    });
  }
  return docs;
}

// The full value of the order = line-item subtotal + tax + shipping. Used for
// the order's "Total" display.
// Dollar value of the order's discount against a given item subtotal.
// Percentage discounts scale with the subtotal, so partial receives can pass
// their received subtotal and get the proportional share; fixed discounts
// prorate by the subtotal's share of the full order.
export function getPurchaseOrderDiscountValue(
  order: PurchaseOrder,
  subtotal: number = order.totalCost
): number {
  if (!order.discount) return 0;
  const value =
    order.discountType === "percentage"
      ? subtotal * (order.discount / 100)
      : order.totalCost > 0
      ? order.discount * (subtotal / order.totalCost)
      : 0;
  return Math.round(value * 100) / 100;
}

export function getPurchaseOrderTotal(order: PurchaseOrder): number {
  return (
    Math.round(
      (order.totalCost -
        getPurchaseOrderDiscountValue(order) +
        (order.tax ?? 0) +
        (order.shipping ?? 0)) *
        100
    ) / 100
  );
}

// What actually hits Money Spent: the value of the units received so far
// (cash basis — you expense what you've received/paid), plus tax + shipping.
// Accrues across partial receives. Zero until something is received.
export function getPurchaseOrderReceivedValue(order: PurchaseOrder): number {
  const receivedSubtotal = order.items.reduce(
    (sum, i) => sum + i.unitPrice * i.quantityReceived,
    0
  );
  if (receivedSubtotal <= 0) return 0;
  return (
    Math.round(
      (receivedSubtotal -
        getPurchaseOrderDiscountValue(order, receivedSubtotal) +
        (order.tax ?? 0) +
        (order.shipping ?? 0)) *
        100
    ) / 100
  );
}

// Settle a partially received order at what actually arrived (e.g. supplier
// short-shipped and the rest isn't coming). Flips it to Received without
// receiving more; Money Spent already reflects the received value.
export async function closePurchaseOrderShort(
  userId: string,
  orderUid: string,
  teamMember?: TeamMember
): Promise<void> {
  const docRef = doc(db, "users", userId, "purchaseOrders", orderUid);
  const snap = await getDoc(docRef);
  const alreadyReceivedAt =
    (snap.data() as PurchaseOrder | undefined)?.receivedAt ?? null;
  await updateDoc(docRef, {
    status: PurchaseOrderStatus.Received,
    // Closing short settles what already arrived, it is not a new delivery, so
    // keep the date the goods actually came in. Overwriting it here would drag
    // a backdated order's spend into the month it was settled. Only an order
    // with no received date at all gets stamped.
    ...(alreadyReceivedAt ? {} : { receivedAt: Timestamp.now() }),
    deductedAsExpense: true,
    history: arrayUnion(
      poHistory(teamMember, PurchaseOrderStatus.Received)
    ),
  });
}
